import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box, Typography, Alert, Table, TableHead, TableBody, TableRow, TableCell,
  Paper, Button, IconButton, Tooltip, Chip, Skeleton,
} from '@mui/material'
import {
  TableChart, Add, CreateNewFolder, VisibilityOff, Visibility,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePlantContext } from '../hooks/usePlantContext'
import { keepOrSelectNewest } from '../utils/folderSelection'
import { sortByRecentActivity, hiddenCount } from '../utils/planActivity'
import { basePlanShortLabel } from '../utils/basePlanLabel'
import { formatDate, formatRelativeTime } from '../utils/format'
import { api } from '../services/api'
import FolderSelector from '../components/plans/FolderSelector'
import CreateFolderModal from '../components/plans/CreateFolderModal'
import CreatePlanModal from '../components/plans/CreatePlanModal'
import BasePlanScheduleNotice from '../components/plans/BasePlanScheduleNotice'

// FAR-55: a single list of Production Plans.
//
// Columns are Production Plan Name, Base Plan (timestamp) and Modification
// (timestamp + created-by), sorted newest first by the later of the two
// timestamps. Clicking a row opens that plan's Planning Board.
//
// The card/grid toggle is gone (the columns don't fit cards), as are the
// Filters button and the Copy / Duplicate / Approve / Archive / Delete row
// actions. The only row actions are open and Hide/Restore. Approval lives on
// the Planning Board (FAR-61).
//
// The old demo-plan fallback is gone too: an empty list now says it's empty
// rather than showing invented plans.

function ModificationCell({ plan }) {
  if (!plan.modificationListCreatedAt) {
    return <Typography variant="body2" color="text.secondary">—</Typography>
  }
  return (
    <>
      <Typography variant="body2">
        {formatDate(plan.modificationListCreatedAt)}
        {plan.modificationListName ? ` · ${plan.modificationListName}` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {plan.modificationListCreatedBy || 'unknown'} · {formatRelativeTime(plan.modificationListCreatedAt)}
      </Typography>
    </>
  )
}

export default function PlanList() {
  const navigate = useNavigate()
  const { plant } = usePlantContext()

  const [plans, setPlans] = useState([])
  const [folders, setFolders] = useState([])
  const [selectedFolderId, setSelectedFolderId] = useState('')
  const [showHidden, setShowHidden] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [saving, setSaving] = useState(false)

  const [folderOpen, setFolderOpen] = useState(false)
  const [folderError, setFolderError] = useState(null)
  const [savingFolder, setSavingFolder] = useState(false)

  // Folders and plans load separately (FAR-54). Folder loading SETS the
  // selected folder and plan loading DEPENDS on it, so one combined callback
  // would change its own dependency -- the FAR-44 render-loop shape.
  const loadFolders = useCallback(async () => {
    try {
      const data = await api.get(`/folders?plant=${plant.plantCode}`)
      const loaded = Array.isArray(data) ? data : []
      setFolders(loaded)
      setSelectedFolderId((current) => keepOrSelectNewest(loaded, current))
    } catch {
      setFolders([])
    }
  }, [plant.plantCode])

  // Hidden plans are always fetched so "Show hidden (N)" has an accurate N;
  // the toggle only decides whether they are rendered.
  const loadPlans = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const query = new URLSearchParams({ plant: plant.plantCode, includeHidden: 'true' })
      if (selectedFolderId) query.set('folderId', selectedFolderId)
      const data = await api.get(`/plans?${query}`)
      setPlans(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message || 'Could not load plans')
      setPlans([])
    } finally {
      setLoading(false)
    }
  }, [plant.plantCode, selectedFolderId])

  useEffect(() => { loadFolders() }, [loadFolders])
  useEffect(() => { loadPlans() }, [loadPlans])

  const hidden = useMemo(() => hiddenCount(plans), [plans])
  const rows = useMemo(() => {
    const visible = showHidden ? plans : plans.filter((p) => !p.hidden)
    return sortByRecentActivity(visible)
  }, [plans, showHidden])

  const handleCreatePlan = async (data) => {
    setCreateError(null)
    setSaving(true)
    try {
      const created = await api.post('/plans', data)
      setCreateOpen(false)
      // FAR-70: go straight into the new plan's Planning Board.
      if (created?.id) navigate(`/plans/${created.id}/grid`)
      else loadPlans()
    } catch (err) {
      setCreateError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleCreateFolder = async ({ name, description }) => {
    setFolderError(null)
    setSavingFolder(true)
    try {
      const created = await api.post('/folders', { name, description, plant: plant.plantCode })
      setFolders((prev) => [...prev.filter((f) => f.id !== created.id), created])
      setSelectedFolderId(created.id)
      setFolderOpen(false)
    } catch (err) {
      setFolderError(err.message || 'Could not create the folder')
    } finally {
      setSavingFolder(false)
    }
  }

  // Hide/Restore never deletes: it flips a flag, and the plan stays listed
  // under "Show hidden".
  const setHidden = async (plan, nextHidden) => {
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, hidden: nextHidden } : p)))
    try {
      await api.patch(`/plans/${plan.id}/hidden`, { hidden: nextHidden, plant: plant.plantCode })
    } catch (err) {
      // Put it back: the list must not claim a change the server rejected.
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, hidden: !nextHidden } : p)))
      setError(err.message || `Could not ${nextHidden ? 'hide' : 'restore'} the plan`)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TableChart color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Plans</Typography>
      </Box>

      {/* FAR-58: when Base Plans are built automatically */}
      <BasePlanScheduleNotice plantCode={plant?.plantCode} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <FolderSelector
          folders={folders}
          selectedFolderId={selectedFolderId}
          onSelect={setSelectedFolderId}
          onCreateFolder={() => { setFolderError(null); setFolderOpen(true) }}
        />

        {/* FAR-54: beside the dropdown, outlined -- "Create a Plan" stays the
            screen's main action. */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<CreateNewFolder />}
          onClick={() => { setFolderError(null); setFolderOpen(true) }}
        >
          New Folder
        </Button>

        <Box sx={{ flex: 1 }} />

        {hidden > 0 && (
          <Button
            size="small"
            variant={showHidden ? 'contained' : 'outlined'}
            startIcon={showHidden ? <Visibility /> : <VisibilityOff />}
            onClick={() => setShowHidden((v) => !v)}
          >
            Show hidden ({hidden})
          </Button>
        )}

        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Create a Plan
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>{error}</Alert>}

      {loading && plans.length === 0 ? (
        <Skeleton variant="rounded" height={260} />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Production Plan</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Base Plan</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Modification</TableCell>
                <TableCell align="right" sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((plan) => (
                <TableRow
                  key={plan.id}
                  hover
                  onClick={() => navigate(`/plans/${plan.id}/grid`)}
                  sx={{ cursor: 'pointer', opacity: plan.hidden ? 0.55 : 1 }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {plan.name}
                      {plan.hidden && (
                        <Chip label="Hidden" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Typography>
                    {plan.description && (
                      <Typography variant="caption" color="text.secondary">{plan.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={plan.basePlanCreatedAt ? 'text.primary' : 'text.secondary'}>
                      {plan.basePlanId
                        ? basePlanShortLabel({ id: plan.basePlanId, createdAt: plan.basePlanCreatedAt })
                        : '—'}
                    </Typography>
                    {plan.basePlanTrigger && (
                      <Typography variant="caption" color="text.secondary">
                        {plan.basePlanTrigger === 'scheduled' ? 'daily run' : 'generated'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><ModificationCell plan={plan} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title={plan.hidden ? 'Restore' : 'Hide'}>
                      <IconButton
                        size="small"
                        aria-label={plan.hidden ? 'Restore plan' : 'Hide plan'}
                        onClick={(e) => { e.stopPropagation(); setHidden(plan, !plan.hidden) }}
                      >
                        {plan.hidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hidden > 0 && !showHidden
                        ? `No visible plans. ${hidden} hidden — use "Show hidden" to see them.`
                        : 'No plans in this folder yet. Create a Plan to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <CreateFolderModal
        open={folderOpen}
        onClose={() => { setFolderOpen(false); setFolderError(null) }}
        onSubmit={handleCreateFolder}
        loading={savingFolder}
        error={folderError}
      />
      <CreatePlanModal
        plant={plant}
        open={createOpen}
        onClose={() => { setCreateOpen(false); setCreateError(null) }}
        onSubmit={handleCreatePlan}
        folders={folders}
        defaultFolderId={selectedFolderId}
        onCreateFolder={handleCreateFolder}
        loading={saving}
        error={createError}
      />
    </Box>
  )
}
